import React from "react";
import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import calibrationApi from "../api/calibrationApi";
import ModulePageShell from "../components/common/ModulePageShell";
import LoadingState from "../components/common/LoadingState";
import EmptyState from "../components/common/EmptyState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";

const CalibrationDetailPage = () => {
  const { id } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadCalibrations = can(PERFORMANCE_PERMISSIONS.CALIBRATIONS_READ);
  const canManageCalibrations = can(
    PERFORMANCE_PERMISSIONS.CALIBRATIONS_MANAGE,
  );

  if (!canReadCalibrations) {
    return (
      <ModulePageShell
        title="Calibration Workspace"
        description="Align employee ratings, capture discussion notes, and finalize calibration."
      >
        <EmptyState message="You do not have permission to view calibration details." />
      </ModulePageShell>
    );
  }

  const detailQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.calibrationDetail, id],
    queryFn: () => calibrationApi.getCalibrationById(id),
    enabled: Boolean(id),
  });

  const finalizeMutation = useMutation({
    mutationFn: () => calibrationApi.finalizeCalibration(id),
    onSuccess: () => {
      toast({
        title: "Calibration finalized",
        status: "success",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.calibrationDetail, id],
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to finalize",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const item = detailQuery.data?.data;
  const employees = item?.employees || [];

  return (
    <ModulePageShell
      title="Calibration Workspace"
      description="Align employee ratings, capture discussion notes, and finalize calibration."
      onRefresh={() => detailQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
      actions={
        <Button
          size="sm"
          colorScheme="green"
          onClick={() => finalizeMutation.mutate()}
          isDisabled={!item || item.finalized || !canManageCalibrations}
          isLoading={finalizeMutation.isPending}
        >
          Finalize Session
        </Button>
      }
    >
      {detailQuery.isLoading ? (
        <LoadingState />
      ) : !item ? (
        <EmptyState message="Calibration session not found." />
      ) : (
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
          <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="bold" mb={2}>
              Employee List
            </Text>
            <VStack align="stretch" spacing={2}>
              {employees.map((emp) => (
                <Box
                  key={emp._id || emp.employeeId}
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                >
                  <Text fontSize="sm">
                    {emp.employeeId?.name || emp.employeeId}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="bold" mb={2}>
              Ratings Matrix
            </Text>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Employee</Th>
                  <Th>Original</Th>
                  <Th>Calibrated</Th>
                </Tr>
              </Thead>
              <Tbody>
                {employees.map((emp) => (
                  <Tr key={emp._id || emp.employeeId}>
                    <Td>{emp.employeeId?.name || emp.employeeId}</Td>
                    <Td>{emp.originalRating ?? "-"}</Td>
                    <Td>{emp.calibratedRating ?? "-"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="bold" mb={2}>
              Discussion Notes
            </Text>
            <Text color="gray.600" fontSize="sm">
              Use employee-level reason fields while creating/updating sessions
              to capture calibration rationale.
            </Text>
            <HStack mt={3}>
              <Text fontSize="sm">
                <strong>Department:</strong> {item.department}
              </Text>
              <Text fontSize="sm">
                <strong>Status:</strong> {item.finalized ? "FINALIZED" : "OPEN"}
              </Text>
            </HStack>
          </Box>
        </SimpleGrid>
      )}
    </ModulePageShell>
  );
};

export default CalibrationDetailPage;
